/**
 * SharedPropertyPage — public route: /shared/:token
 *
 * This page is intentionally outside AuthGuard (see App.tsx).
 * Receivers do NOT need a Ridgen account to view a shared property.
 * Access is validated server-side by the shared-property Edge Function using
 * the share token. The Edge Function reads data via the service role key
 * (bypassing RLS) and returns only what the owner chose to share
 * (locations, and optionally scouting log entries + photos).
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import { format, parseISO } from 'date-fns';

import { formatTagLabel } from '@/utils/format-utils';
import type { SharedPropertyData, SharedPropertyResponse, SharedScoutingEntry } from '@/types/share';
import { HuntMap } from '@/components/map/HuntMap';
import { useMap } from '@/hooks/useMap';

// ── Location pin markers (rendered inside MapProvider) ────────────────────────

interface LocationPinsProps {
  locations: SharedPropertyData['locations'];
}

function LocationPins({ locations }: LocationPinsProps) {
  const map = useMap();

  useEffect(() => {
    const markers: maplibregl.Marker[] = [];

    for (const loc of locations) {
      const el = document.createElement('div');
      // SAFETY: SVG color is a constant hex string, not user input
      el.innerHTML = `<svg width="20" height="28" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20S24 21 24 12C24 5.373 18.627 0 12 0z"
          fill="#6B7280" stroke="rgba(255,255,255,0.75)" stroke-width="1.5"/>
        <circle cx="12" cy="12" r="4" fill="rgba(255,255,255,0.9)"/>
      </svg>`;
      // SAFETY: firstElementChild always exists — html has a root <svg>
      const svg = el.firstElementChild as HTMLElement;

      const popupContent = document.createElement('div');
      const nameEl = document.createElement('strong');
      nameEl.textContent = loc.name;
      popupContent.appendChild(nameEl);
      if (loc.notes) {
        popupContent.appendChild(document.createElement('br'));
        // textContent (not innerHTML) keeps user-supplied notes inert — see issue #1293
        popupContent.appendChild(document.createTextNode(loc.notes));
      }

      const popup = new maplibregl.Popup({ offset: 25, closeButton: false })
        .setDOMContent(popupContent);

      const marker = new maplibregl.Marker({ element: svg, anchor: 'bottom' })
        .setLngLat([loc.longitude, loc.latitude])
        .setPopup(popup)
        .addTo(map);

      markers.push(marker);
    }

    return () => {
      for (const m of markers) m.remove();
    };
  }, [map, locations]);

  return null;
}

// ── Subcomponents ─────────────────────────────────────────────────────────────

function ScoutingTimeline({ entries }: { entries: SharedScoutingEntry[] }) {
  const sorted = [...entries].sort((a, b) => b.date.localeCompare(a.date));

  if (sorted.length === 0) {
    return (
      <p className="text-body-sm text-[var(--color-text-muted)]">No scouting entries shared</p>
    );
  }

  return (
    <div className="relative flex flex-col" data-testid="scouting-timeline">
      <div
        className="absolute left-[5px] top-2 bottom-2 w-px bg-[var(--color-border)]"
        aria-hidden="true"
      />
      {sorted.map((entry, index) => {
        const showDateMarker = index === 0 || entry.date !== sorted[index - 1]?.date;
        return (
          <div key={entry.id} className="relative flex flex-col">
            {showDateMarker && (
              <div className="flex items-center gap-3 mb-2 mt-4 first:mt-0">
                <div className="relative z-10 w-2.5 h-2.5 rounded-full bg-[var(--color-accent)] shrink-0" />
                <span className="field-label pl-1">
                  {format(parseISO(entry.date), 'MMM d, yyyy')}
                </span>
              </div>
            )}
            <div className="ml-[22px] mb-3">
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3 flex flex-col gap-2">
                {entry.locationName && (
                  <span className="text-body-xs text-[var(--color-accent)] font-medium">
                    {entry.locationName}
                  </span>
                )}
                {entry.time && (
                  <span className="text-body-xs text-[var(--color-text-muted)]">{entry.time}</span>
                )}
                {entry.notes && (
                  <p className="text-body-sm text-[var(--color-text-primary)] leading-snug">
                    {entry.notes}
                  </p>
                )}
                {entry.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {entry.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-body-xs border border-[var(--color-border-subtle)] text-[var(--color-text-secondary)]"
                      >
                        {formatTagLabel(tag)}
                      </span>
                    ))}
                  </div>
                )}
                {entry.animalObservation?.species && (
                  <p className="text-body-xs text-[var(--color-accent-hover)]">
                    &#x1F98C;{' '}
                    {[
                      entry.animalObservation.count,
                      entry.animalObservation.sex,
                      entry.animalObservation.species,
                      entry.animalObservation.antlerPoints
                        ? `(${entry.animalObservation.antlerPoints}-pt)`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PasswordForm({ onSubmit, wrongPassword }: { onSubmit: (pw: string) => void; wrongPassword?: boolean }) {
  const [password, setPassword] = useState('');
  const [hasAttempted, setHasAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!password.trim()) return;
    setHasAttempted(true);
    setIsSubmitting(true);
    try {
      onSubmit(password);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 gap-6" data-testid="password-form">
      <div className="w-full max-w-sm flex flex-col gap-4">
        <div className="text-center">
          <p className="font-display text-display-sm text-[var(--color-text-display)]">
            Password required
          </p>
          <p className="text-body-sm text-[var(--color-text-muted)] mt-1">
            This share link is password protected.
          </p>
        </div>
        <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-3">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            className="w-full px-3 py-2.5 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
            data-testid="password-field"
            autoFocus
          />
          {hasAttempted && wrongPassword && (
            <p className="text-body-xs text-red-400" data-testid="password-error">Incorrect password — please try again.</p>
          )}
          <button
            type="submit"
            disabled={isSubmitting || !password.trim()}
            className="w-full py-2.5 rounded-md bg-[var(--color-accent)] text-[var(--color-text-display)] text-body-sm font-medium transition-colors hover:bg-[var(--color-accent-hover)] disabled:opacity-50"
          >
            {isSubmitting ? 'Checking…' : 'View property'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Shared property data state ────────────────────────────────────────────────

type PageState =
  | { status: 'loading' }
  | { status: 'password-required'; wrongPassword?: boolean }
  | { status: 'error'; message: string }
  | { status: 'loaded'; data: SharedPropertyData };

// ── Main page component ───────────────────────────────────────────────────────

export function SharedPropertyPage() {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>({ status: 'loading' });

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;

  async function fetchSharedData(password?: string) {
    if (!supabaseUrl || !token) {
      setPageState({ status: 'error', message: 'This link appears to be invalid.' });
      return;
    }

    const url = `${supabaseUrl}/functions/v1/shared-property/${token}`;
    const method = password ? 'POST' : 'GET';
    const body = password ? JSON.stringify({ password }) : undefined;

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body,
      });

      if (res.status === 404) {
        setPageState({ status: 'error', message: 'This link has expired or is no longer active.' });
        return;
      }

      if (res.status === 401) {
        setPageState({ status: 'password-required', wrongPassword: true });
        return;
      }

      const json = (await res.json()) as SharedPropertyResponse;

      if (json.passwordRequired) {
        setPageState({ status: 'password-required' });
        return;
      }

      setPageState({ status: 'loaded', data: json });
    } catch {
      setPageState({ status: 'error', message: 'Unable to load this property. Please try again.' });
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async load dispatched from effect body; setState is inside callback
    void fetchSharedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Render states ──────────────────────────────────────────────────────────

  if (pageState.status === 'loading') {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
          <p className="text-body-sm text-[var(--color-text-muted)]">Loading property…</p>
        </div>
      </div>
    );
  }

  if (pageState.status === 'error') {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex flex-col items-center justify-center gap-4 px-4" data-testid="error-page">
        <p className="font-display text-display-sm text-[var(--color-text-display)] text-center">
          {pageState.message}
        </p>
        <Link
          to="/"
          className="text-body-sm text-[var(--color-accent)] hover:opacity-80 transition-opacity"
        >
          Open Ridgen
        </Link>
        <SharedFooter />
      </div>
    );
  }

  if (pageState.status === 'password-required') {
    return (
      <div className="min-h-screen bg-[var(--color-bg)] flex flex-col">
        <PasswordForm
          onSubmit={(pw) => void fetchSharedData(pw)}
          wrongPassword={pageState.wrongPassword}
        />
        <SharedFooter />
      </div>
    );
  }

  const { property, locations, scoutingEntries } = pageState.data;

  // Compute map center from locations or default to center of US
  const hasLocations = locations.length > 0;
  const center: [number, number] = hasLocations
    ? [
        locations.reduce((sum, l) => sum + l.latitude, 0) / locations.length,
        locations.reduce((sum, l) => sum + l.longitude, 0) / locations.length,
      ]
    : [44.0, -90.0];

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-[var(--color-text-primary)] flex flex-col">
      {/* Header */}
      <header className="bg-[var(--color-surface)] border-b border-[var(--color-border)] px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <p className="text-body-xs text-[var(--color-text-muted)] mb-0.5">Shared via Ridgen</p>
          <h1
            className="font-display text-display-lg text-[var(--color-text-display)]"
            data-testid="property-name"
          >
            {property.name}
          </h1>
          {property.state && (
            <p className="text-body-sm text-[var(--color-text-muted)] mt-0.5">{property.state}</p>
          )}
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-4 py-6 max-w-2xl mx-auto w-full flex flex-col gap-6">
        {/* Map */}
        {hasLocations && (
          <section>
            <p className="field-label mb-2">
              Map
            </p>
            <div
              className="h-72 md:h-96 rounded-lg overflow-hidden border border-[var(--color-border)]"
              data-testid="shared-map"
            >
              <HuntMap
                center={center}
                zoom={14}
                className="h-full w-full"
                initialStyle="satellite"
              >
                <LocationPins locations={locations} />
              </HuntMap>
            </div>
          </section>
        )}

        {/* Locations list */}
        <section data-testid="locations-section">
          <p className="field-label mb-3">
            Locations ({locations.length})
          </p>
          {locations.length === 0 ? (
            <p className="text-body-sm text-[var(--color-text-muted)]">No locations on this property.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {locations.map((loc) => (
                <div
                  key={loc.id}
                  className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-3 flex flex-col gap-1"
                  data-testid={`location-card-${loc.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-body-sm font-medium text-[var(--color-text-primary)]">
                      {loc.name}
                    </span>
                    <span className="text-body-xs text-[var(--color-text-muted)] capitalize">
                      {loc.type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  {loc.notes && (
                    <p className="text-body-xs text-[var(--color-text-secondary)]">{loc.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Scouting log */}
        {scoutingEntries.length > 0 && (
          <section data-testid="scouting-section">
            <p className="field-label mb-3">
              Scouting Log ({scoutingEntries.length})
            </p>
            <ScoutingTimeline entries={scoutingEntries} />
          </section>
        )}
      </main>

      <SharedFooter />
    </div>
  );
}

function SharedFooter() {
  return (
    <footer className="border-t border-[var(--color-border)] px-4 py-5 text-center" data-testid="shared-footer">
      <p className="text-body-xs text-[var(--color-text-muted)]">
        Powered by{' '}
        <Link
          to="/"
          className="text-[var(--color-accent)] hover:opacity-80 font-medium"
        >
          Ridgen
        </Link>
      </p>
    </footer>
  );
}
